/**
 * productMode — which Dividia product line the viewer is serving.
 *
 * This is the dimension that picks a ViewerBackend adapter. It is DISTINCT from
 * eView: eView is the LOCAL backend operating across multiple member NVRs (see
 * util/sessionMode.isMultiServerSession) — still product mode 'local'. Cloud and
 * EEN are genuinely different backends (REST + JWT) and land in later chunks.
 *
 * Today the viewer only serves the local NVR, so getProductMode() is constant.
 * It's a selector (not a constant) so cloud/EEN can flip it from state — e.g.
 * how the app was loaded (nvr.cloud.dividia.net) — without changing call sites.
 */
export const PRODUCT_MODE = Object.freeze({
  LOCAL: 'local',
  CLOUD: 'cloud',
  EEN: 'een',
});

const VALID_MODES = new Set(Object.values(PRODUCT_MODE));

/**
 * Resolve the product mode, in priority order:
 *   1. an explicit Redux override (`state.productMode`) — lets the app flip mode
 *      at runtime/bootstrap and makes the selector trivially testable;
 *   2. the build-time env `REACT_APP_PRODUCT_MODE` — cloud is a SEPARATE deploy
 *      (the nvr.cloud host swap), so its build bakes `=cloud`; local builds leave
 *      it unset (same `REACT_APP_*` convention as REACT_APP_SERVER_HOST/VERSION);
 *   3. LOCAL — the default, so every existing (non-cloud) build is unchanged.
 *
 * @param {Object} [state] - Redux state
 * @returns {string} one of PRODUCT_MODE
 */
export const getProductMode = (state) => {
  const override = state?.productMode;
  if (override && VALID_MODES.has(override)) return override;

  const env = process.env.REACT_APP_PRODUCT_MODE;
  if (env && VALID_MODES.has(env)) return env;

  return PRODUCT_MODE.LOCAL;
};
